import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_DARK = "https://customer-assets.emergentagent.com/job_4cb3e094-2c6c-4bf9-b6ba-50cf8fe1eb94/artifacts/zh1bc2r6_opensapiens_logo_transparent_dark.png";

const NAV = [
  { to: '/legal/cgu', label: "CGU", slug: 'cgu' },
  { to: '/legal/privacy', label: "Confidentialité", slug: 'privacy' },
  { to: '/legal/mentions', label: "Mentions légales", slug: 'mentions' },
];

/**
 * LegalLayout — wrapper partagé par les 3 pages légales publiques.
 * Phase Beta : bandeau "Conditions susceptibles d'évoluer" affiché en
 * permanence pour gérer les attentes (cf. décision Steve, sprint 1.0a).
 *
 * Sprint 1.0b-γ — `slug` prop ajouté pour fetcher automatiquement la date
 * de dernière mise à jour via `/api/meta/legal/{slug}/last-updated` (basée
 * sur le mtime du fichier source). Plus de date codée en dur.
 */
const LegalLayout = ({ title, lastUpdated, slug, children }) => {
  const location = useLocation();
  const [autoUpdatedFr, setAutoUpdatedFr] = useState('');

  useEffect(() => {
    if (!slug) return;
    axios
      .get(`${API}/meta/legal/${slug}/last-updated`)
      .then((r) => setAutoUpdatedFr(r.data?.last_updated_human_fr || ''))
      .catch(() => {});
  }, [slug]);

  const displayedDate = autoUpdatedFr || lastUpdated;

  return (
    <div className="min-h-screen bg-[#0d1f35] text-gray-200" data-testid="legal-page">
      {/* Nav */}
      <nav className="border-b border-white/5">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img src={LOGO_DARK} alt="" className="h-8 w-8 object-contain" />
            <span className="text-lg font-semibold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              OpenSapiens
            </span>
          </Link>
          <Link
            to="/"
            data-testid="legal-back-home"
            className="text-sm text-gray-400 hover:text-white inline-flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Retour à l'accueil
          </Link>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-10 max-w-4xl">
        {/* Beta banner — sprint 1.0a, requested by Steve */}
        <div
          data-testid="legal-beta-banner"
          className="mb-8 flex items-start gap-3 p-4 rounded-xl border border-amber-400/30 bg-amber-500/10"
        >
          <Sparkles className="h-5 w-5 text-amber-300 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-100">
            <strong className="font-semibold">Phase Beta —</strong> Les présentes conditions sont
            susceptibles d'évoluer pendant la phase de lancement. Toute modification importante vous
            sera notifiée par email.
          </div>
        </div>

        {/* Inter-page nav */}
        <div className="flex flex-wrap gap-2 mb-8">
          {NAV.map((n) => {
            const active = location.pathname === n.to;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`legal-nav-${n.to.split('/').pop()}`}
                className={`px-3 py-1.5 rounded-lg text-sm border transition-all ${
                  active
                    ? 'bg-cyan-500/20 border-cyan-400/50 text-cyan-200'
                    : 'border-white/10 text-gray-400 hover:text-white hover:border-white/30'
                }`}
              >
                {n.label}
              </Link>
            );
          })}
        </div>

        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white">{title}</h1>
          {displayedDate && (
            <p className="text-xs text-gray-500 mt-2">Dernière mise à jour : {displayedDate}</p>
          )}
        </header>

        <div className="prose prose-invert max-w-none legal-content">{children}</div>
      </div>

      <footer className="border-t border-white/5 py-8 mt-12">
        <div className="container mx-auto px-6 text-center text-sm text-gray-500">
          © 2026 OpenSapiens · Genève, Suisse
        </div>
      </footer>

      <style>{`
        .legal-content h2 { color: #fff; font-size: 1.25rem; font-weight: 700; margin-top: 2rem; margin-bottom: 0.5rem; }
        .legal-content h3 { color: #e5e7eb; font-size: 1.05rem; font-weight: 600; margin-top: 1.25rem; margin-bottom: 0.5rem; }
        .legal-content p, .legal-content li { color: #cbd5e1; line-height: 1.7; }
        .legal-content ul { list-style: disc; padding-left: 1.5rem; margin: 0.5rem 0 1rem; }
        .legal-content ol { list-style: decimal; padding-left: 1.5rem; margin: 0.5rem 0 1rem; }
        .legal-content a { color: #67e8f9; text-decoration: underline; }
        .legal-content strong { color: #f1f5f9; }
      `}</style>
    </div>
  );
};

export default LegalLayout;
